import { getAccessToken } from './auth-tokens';

/**
 * Centralized WebSocket logger.
 * Wraps console calls so production suppression can be handled in one place
 * (e.g., via Vite's `esbuild.drop: ['console']` in vite.config.ts).
 */
const wsLog = {
  info: (...args: unknown[]) => console.log(...args),
  warn: (...args: unknown[]) => console.warn(...args),
  error: (...args: unknown[]) => console.error(...args),
};

/**
 * Callback type for WebSocket event handlers.
 */
export type EventHandler = (data: unknown) => void;

/**
 * Derives the WebSocket base URL from the BFF HTTP URL.
 * Replaces `http` → `ws` (also handles `https` → `wss`).
 */
const WS_BASE_URL = (import.meta.env.VITE_BFF_URL || 'http://localhost:3010').replace(
  /^http/,
  'ws',
);

/**
 * WebSocketManager handles real-time communication with the BFF WebSocket gateway.
 *
 * Features:
 * - JWT authentication via `?token=<jwt>` query param (matches BFF EventsGateway)
 * - Exponential backoff reconnection (max 10 attempts)
 * - Event-based pub/sub with typed handlers
 * - Clean disconnect with reconnect timer cancellation
 *
 * Adapted from Trinity AI `apps/web/src/lib/websocket.ts` for Zuroq's BFF gateway.
 */
class WebSocketManager {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<EventHandler>>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isConnecting = false;

  /**
   * Establish a WebSocket connection to the BFF gateway.
   * No-ops if already connected or connecting. Requires a valid access token.
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) return;

    const token = getAccessToken();
    if (!token) return;

    this.isConnecting = true;

    try {
      this.ws = new WebSocket(`${WS_BASE_URL}/ws?token=${token}`);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        wsLog.info('[WS] Connected');
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const message = JSON.parse(event.data as string);
          const { event: eventName, data } = message;
          if (eventName) {
            this.emit(eventName, data);
          }
        } catch {
          wsLog.warn('[WS] Failed to parse message:', event.data);
        }
      };

      this.ws.onclose = (event: CloseEvent) => {
        this.isConnecting = false;
        wsLog.info(`[WS] Disconnected (code: ${event.code})`);

        // Don't reconnect on intentional close (1000) or auth failure (4401)
        if (event.code !== 1000 && event.code !== 4401) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = () => {
        this.isConnecting = false;
        wsLog.error('[WS] Connection error');
      };
    } catch {
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Cleanly disconnect the WebSocket connection.
   * Cancels any pending reconnect timer and resets attempt counter.
   */
  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }

  /**
   * Subscribe to a named event. Returns an unsubscribe function.
   * @param event - Event name (e.g. 'notification.new', 'approval.new')
   * @param handler - Callback invoked with the event data payload
   * @returns Unsubscribe function to remove this handler
   */
  on(event: string, handler: EventHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }

  /**
   * Emit an event to all registered handlers for the given event name.
   */
  private emit(event: string, data: unknown): void {
    this.handlers.get(event)?.forEach((handler) => {
      try {
        handler(data);
      } catch (err) {
        wsLog.error(`[WS] Handler error for event "${event}":`, err);
      }
    });
  }

  /**
   * Schedule a reconnection attempt with exponential backoff.
   * Stops after maxReconnectAttempts (10) have been exhausted.
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      wsLog.error('[WS] Max reconnect attempts reached');
      return;
    }

    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    wsLog.info(
      `[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Disconnect and immediately reconnect with the current (new) JWT token.
   * Used after org switch to re-authenticate the WebSocket connection.
   */
  reconnect(): void {
    this.disconnect();
    this.connect();
  }

  /**
   * Whether the WebSocket connection is currently open.
   */
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

/** Singleton WebSocket manager instance. */
export const wsManager = new WebSocketManager();
